import { GoproVideoMetadata, MetadataJsonReader } from "./../../../../../../../../services/MetadataJsonReader";
import { PracticePathService } from "src/app/services/practice-path.service";
import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PracticePath } from "src/app/classes/model/practice-path";
import { MatDialog, MatDialogRef, MatDialogState } from "@angular/material/dialog";
import { NamedFile } from "src/app/services/common/aws-s3.service";
import { FileUploadIndicatorDialogComponent, FileUploadIndicatorDialogData, UploadProgress, } from "src/app/components/misc/file-upload-indicator-dialog/file-upload-indicator-dialog.component";
import { PracticePathUploadDialogComponent } from "../../practice-path-upload-dialog.component";
import { DirectBucketUploadError, DirectBucketUploadProgress, formatTransferredBytes } from "src/app/services/common/direct-bucket-upload.service";
import { ConfirmationDialogService } from "src/app/modules/confirmation-dialog/services/confirmation-dialog.service";

const s3ObjectSanitize = require('sanitize-s3-objectkey');

@Component({
  selector: "app-upload-full-video-tab",
  templateUrl: "./upload-full-video-tab.component.html",
  styleUrls: ["./upload-full-video-tab.component.scss"],
})
export class UploadFullVideoTabComponent implements OnInit {
  @ViewChild("videoInput") videoInput: ElementRef;
  @ViewChild("metadataInput") metadataInput: ElementRef;

  @Input() practicePath: PracticePath;

  readonly allowedVideoExtensions = ["mp4"];
  readonly allowedMetadataExtension = ["json"];

  videoFile: File;
  metadataFile: File;

  isUploading: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<PracticePathUploadDialogComponent>,
    private dialogService: MatDialog,
    private snackBar: MatSnackBar,
    private practicePathService: PracticePathService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {}

  hasValidExtension(file: File, allowedExtension: string[]) {
    if (file == null) return false;
    allowedExtension = allowedExtension.map((a) => a.toLowerCase());
    return allowedExtension.includes(
      file.name.toLowerCase().split(".").pop() ?? ""
    );
  }

  isValidMetadata():boolean {
    const hasValidExtension = this.hasValidExtension(
      this.metadataFile,
      this.allowedMetadataExtension
    );
    if (!hasValidExtension)
      this.snackBar.open(
        "A metadata-nak .JSON kiterjesztésűnek kell lennie.",
        "Bezár",
        { duration: 3500 }
      );

    return hasValidExtension;
  }

  isValidVideo():boolean {
    const hasValidExtension = this.hasValidExtension(
      this.videoFile,
      this.allowedVideoExtensions
    );
    if (!hasValidExtension)
      this.snackBar.open(
        "A videónak .mp4 kiterjesztésűnek kell lennie.",
        "Bezár",
        { duration: 3500 }
      );

    return hasValidExtension;
  }

  onVideoInputChange(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.videoFile = files[0];
    }
  }

  onMetadataInputChange(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.metadataFile = files[0];
    }
  }

  onVideoFileDropped(event: any) {
    const files = event.addedFiles;
    if (files.length > 0) {
      this.videoFile = files[0];
    }
  }

  onMetadataFileDropped(event: any) {
    const files = event.addedFiles;
    if (files.length > 0) {
      this.metadataFile = files[0];
    }
  }

  onVideoFileRemoved() {
    this.videoFile = null;
  }

  onMetadataFileRemoved() {
    this.metadataFile = null;
  }

  async onTapUpload() {
    if (this.isUploading || !this.isValidMetadata() || !this.isValidVideo()) return;

    this.isUploading = true;
    try {
      // The selection may change while the metadata is read, the files are captured here
      await this.uploadFiles(this.videoFile, this.metadataFile);
    } finally {
      this.isUploading = false;
    }
  }

  private async uploadFiles(videoFile: File, metadataFile: File): Promise<void> {
    let metadata: GoproVideoMetadata;
    try {
      metadata = await new MetadataJsonReader().read(metadataFile);
    } catch (error: any) {
      console.error(error);
      this.snackBar.open("A metadata fájl nem olvasható be.", "Bezár", { duration: 3500 });
      return;
    }

    const cancelController: AbortController = new AbortController();
    let cancelConfirmationDialog: MatDialogRef<unknown> | null = null;
    // The confirmation is pointless once the upload can not be cancelled any more
    const closeCancelConfirmation = (): void => {
      if (cancelConfirmationDialog?.getState() === MatDialogState.OPEN) {
        cancelConfirmationDialog.close();
      }
    };
    const uploadProgress: UploadProgress = {
      text: "Feltöltés előkészítése...",
      value: 0,
      cancellable: true,
    };

    const onProgress = (state: "upload" | "save", progress: number, details?: DirectBucketUploadProgress) => {
      if (cancelController.signal.aborted) return;

      uploadProgress.value = progress;
      uploadProgress.cancellable = state == "upload";
      if (state == "upload") {
        const label: string = details?.isResumed ? "Feltöltés folytatása" : "Feltöltés";
        uploadProgress.text = details
          ? `${label} ${progress}% (${formatTransferredBytes(details.uploadedBytes, details.totalBytes)})`
          : `${label} ${progress}%`;
      } else {
        uploadProgress.text = "Videó mentése a szerveren...";
        closeCancelConfirmation();
      }
    };

    const fileUploadDialog: MatDialogRef<FileUploadIndicatorDialogComponent> =
    this.dialogService.open<FileUploadIndicatorDialogComponent, FileUploadIndicatorDialogData>(FileUploadIndicatorDialogComponent, {
      data: {
        uploadProgress: uploadProgress,
        onCancel: () => {
          cancelConfirmationDialog = this.confirmCancelUpload(uploadProgress, cancelController);
        },
      },
      disableClose: true,
    });

    let isSaved: boolean = false;
    let updatedPracticePath:PracticePath|null = null;

    try {
      const namedFile: NamedFile = {
        file: videoFile,
        // A nevét szanetizáljuk, hogy ne legyen benne csak A-Z character
        name: s3ObjectSanitize(videoFile.name),
      };

      updatedPracticePath = await this.practicePathService.uploadFullRawPracticePathVideo(
        this.practicePath.uuid,
        namedFile,
        metadata.geoTaggedFrames,
        onProgress,
        cancelController.signal
      );
      isSaved = true;

      this.snackBar.open("Sikeres feltöltés", "Bezár");
    } catch (error:any) {
      if (DirectBucketUploadError.isCancellation(error)) {
        this.snackBar.open("A feltöltés megszakítva.", "Bezár", { duration: 3500 });
      } else {
        // The error message is already shown, the dialog stays open, so the upload can be retried (resumed)
        console.error(error);
      }
    }

    closeCancelConfirmation();
    fileUploadDialog.close();

    if (isSaved) {
      // The updated path is null if it could not be fetched; the caller reloads the page anyway
      this.dialogRef.close(updatedPracticePath ?? this.practicePath);
    }
  }

  private confirmCancelUpload(uploadProgress: UploadProgress, cancelController: AbortController): MatDialogRef<unknown> {
    return this.confirmationDialogService.open(
      "Feltöltés megszakítása",
      "Biztosan megszakítod a feltöltést? A már feltöltött részek törlődnek, a videót később elölről kell feltölteni.",
      () => {
        // The upload may have finished while the confirmation was open
        if (!uploadProgress.cancellable || cancelController.signal.aborted) return;

        uploadProgress.cancellable = false;
        uploadProgress.text = "Feltöltés megszakítása...";
        cancelController.abort();
      },
      undefined,
      "Megszakítás",
      "Folytatás"
    );
  }
}
